<template>
  <div>
    <div class="referrals-table--wrap">
      <div class="table--wrap" ref="listTable">
        <p class="content__title">{{ $t('referral.period') }}</p>
        <ui-selectbox
          class="some-class"
          :value="duration"
          :items="durationList"
          :width="'175px'"
          :hideLabel="true"
          @input="onDurationChange"
        />
        <app-pagination
          class="clearfix"
          :total="count"
          :page="page"
          :limit="limit"
          :pagingBlock="5"
          @onPagingOptionChange="onPagingOptionChange" />

        <data-table :headers="headers" :sortType="sort" @changeSort="changeSort">
          <template v-slot:colgroup>
            <colgroup>
              <col width="12%">
              <col width="20%">
              <col width="16.5%">
              <col width="14.3%">
              <col width="12.6%">
              <col width="12%">
              <col width="12%">
            </colgroup>
          </template>
          <template v-if="rewardList.length">
            <tbody>
            <tr v-for="(item, index) in rewardList" :key="index">
              <td class="data-referDate">{{ item.referDate | dashDate }}</td>
              <td class="data-userName">{{ item.deleted ? '-' : item.userName }}</td>
              <td class="data-user">{{ item.deleted ? $t('referral.referrals.history.withdrawn') : item.userId }}</td>
              <td class="data-planName">{{ item.serviceType === 'ba' ? 'Journey Map' : 'UX Heatmap' }} ({{ item.planName }})</td>
              <td class="data-reward">{{ item.reward | thousand }} JAM</td>
              <td v-if="item.rewardDate" class="data-rewardDate">{{ item.rewardDate | dashDate }}</td>
              <td v-else class="data-rewardDate awaiting">{{ $t(`referral.referrals.history.awaiting`) }}</td>
              <td :class="['data-rewardState', item.rewardState]">{{ $t(`referral.referrals.history.${item.rewardState}`) }}</td>
            </tr>
            </tbody>
          </template>
          <tbody v-else>
          <tr  class="no-data">
            <td class="no-data" colspan="7">
              <div>
                <i class="icon icon--no-list" />
                <p v-html="$t('referral.referrals.history.noReward')"></p>
              </div>
            </td>
          </tr>
          </tbody>
        </data-table>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { parse, startOfDay, addDays, addMonths, format } from 'date-fns'
import { EventBus } from '@/libs/event-bus'
import { getRewardList } from '@/api/referral'

import AppPagination from '@/components/ui/AppPagination'
import UiSelectbox from '@/components/ui/UiSelectbox'
import DataTable from '@/components/ui/DataTable'

export default {
  components: {
    AppPagination,
    UiSelectbox,
    DataTable
  },
  props: [],
  data () {
    return {
      isLoading: true,
      page: 1,
      limit: 30,
      sort: '-referDate',
      minDate: null,
      maxDate: null,
      dateRange: {
        startDate: null,
        endDate: null
      },
      showDateRangePicker: false,
      headers: [
        {
          name: this.$t('referral.referrals.history.referralDate'),
          sortable: true,
          value: 'referDate'
        },
        {
          name: this.$t('referral.referrals.history.userName'),
          sortable: true,
          value: 'userName'
        },
        {
          name: this.$t('referral.referrals.history.id'),
          sortable: true,
          value: 'userId'
        },
        {
          name: `${this.$t('referral.referrals.history.service')}(${this.$t('common.table.header.planName')})`,
          sortable: true,
          value: 'planName'
        },
        {
          name: this.$t('referral.referrals.history.reward'),
          sortable: true,
          value: 'reward'
        },
        {
          name: this.$t('referral.referrals.history.rewardDate'),
          sortable: true,
          value: 'rewardDate'
        },
        {
          name: this.$t('referral.referrals.history.status'),
          sortable: true,
          value: 'rewardState',
          tooltip: this.$t('referral.referrals.history.statusTooltip')
        }
      ],
      duration: 0,
      durationList: [
        {
          value: 0,
          text: this.$t('common.all')
        },
        {
          value: 3,
          text: this.$t('common.recentMonths', { month: '3' })
        },
        {
          value: 6,
          text: this.$t('common.recentMonths', { month: '6' })
        },
        {
          value: 12,
          text: this.$t('common.recentMonths', { month: '12' })
        },
        {
          value: 24,
          text: this.$t('common.recentMonths', { month: '24' })
        }
      ],
      rewardList: [],
      count: 0
    }
  },
  computed: {
    ...mapState([
      'userData',
      'planData'
    ])
  },
  created () {
    const today = startOfDay(new Date())
    const minDate = addDays(startOfDay(parse(this.userData.regDate)), -1)
    const maxDate = parse(today)
    const endDate = parse(today)
    let startDate = minDate

    if (startDate < minDate) {
      startDate = parse(minDate)
    }

    this.minDate = minDate
    this.maxDate = maxDate

    this.dateRange = {
      startDate,
      endDate
    }
    this.loadData()
  },
  mounted () {
    EventBus.$on('closeDateRangePicker', () => {
      this.showDateRangePicker = false
    })
  },
  methods: {
    loadData () {
      this.isLoading = true
      const startDate = this.dateRange.startDate
      const endDate = this.dateRange.endDate

      const payload = {
        startDate: null,
        endDate: null,
        page: this.page - 1,
        limit: this.limit,
        order: this.sort
      }

      if (startDate && endDate) {
        payload.startDate = format(startDate, 'YYYY-MM-DD')
        payload.endDate = format(endDate, 'YYYY-MM-DD')
      }

      getRewardList(payload).then(res => {
        if (res) {
          this.rewardList = res.list
          this.count = res.count
        }
      }).catch(e => {
        console.log(e)
      }).finally(() => {
        this.isLoading = false
      })
    },
    closePicker () {
      this.showDateRangePicker = false
    },
    onRangeChange (data) {
      this.dateRange = data.range
      this.loadData()
      this.closePicker()
    },
    onPagingOptionChange (page, limit) {
      this.page = page
      this.limit = limit
      this.loadData()
    },
    onDurationChange (duration) {
      const today = startOfDay(new Date())
      const minDate = addDays(startOfDay(parse(this.userData.regDate)), -1)
      const endDate = parse(today)
      let startDate = addMonths(today, -duration)
      if (duration === 0) {
        startDate = addDays(parse(this.userData.regDate), -1)
      }

      if (startDate < minDate) {
        startDate = parse(minDate)
      }

      this.duration = duration
      this.page = 1

      this.onRangeChange({
        range: {
          startDate,
          endDate
        }
      })
    },
    changeSort (sort) {
      this.sort = sort
      this.page = 1
      this.loadData()
    }
  }
}
</script>

<style lang="scss" scoped="scoped">
.referrals-table--wrap {
  padding: 15px 25px 30px 25px;
  margin-top: 30px;
  box-sizing: border-box;
  border-radius: 14px;
  box-shadow: 0 23px 15px -12px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(0, 0, 0, 0.08);
  background-color: #ffffff;

  .content {
    text-align: center;
  }
  .content__title {
    font-size: 15px;
    font-weight: 500;
    color: #2f2f2f;
    display: inline-block;
    padding: 11px 0 20px 0;
  }

  .ui_select {
    vertical-align: middle;
    transform: translateY(-2px);
    margin-left: 10px;
    position: relative;
    z-index: 2;
  }

  .page__comm--wrap {
    float: right;
    width: auto;
    margin-bottom: 15px;
  }

  .table--wrap {
    /deep/ table {
      thead {
        tr {
          background-color: #333;
          th {
            padding: 6px 12px 8px 12px;
            text-align: start;
            vertical-align: bottom;
            box-sizing: border-box;
            span {
              line-height: 1.2;
            }
            &:first-of-type:before,
            &:last-of-type:before {
              background-color: #333;
            }
            &.data-referDate {
              text-align: center;
            }
            &.data-planName {
              text-align: center;
            }
            &.data-reward {
              text-align: end;
            }
            &.data-rewardDate {
              text-align: center;
            }
            &.data-rewardState {
              text-align: center;
            }
          }
        }
      }
    }
    tbody tr {
      border-top: 1px solid #ebebeb;
      text-align: start;

      &:first-child {
        border-top: none
      }

      &:last-child {
        border-bottom: 1px solid #ebebeb;

        &.no-data {
          border-bottom: none;
        }
      }

      td {
        height: 60px;
        padding: 12px;
        font-size: 13px;
        line-height: 1.2;
        color: #444;
        position: relative;
        box-sizing: border-box;

        &.data-referDate {
          text-align: center;
        }
        &.data-planName {
          text-align: center;
        }
        &.data-reward {
          text-align: end;
        }
        &.data-rewardDate {
          text-align: center;
          &.awaiting {
            color: #888;
          }
        }
        &.data-rewardState {
          text-align: center;
          font-weight: 600;
          &.pending {
            color: #9665E3;
          }
          &.confirmed {
            color: #57AB00;
          }
        }
      }
    }
  }
}

.no-data {
  height: 100px;
  padding-top: 15px;
  position: relative;
  & > div {
    width: 100%;
    min-height: 366px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;

    &> p {
      text-align: center;
    }
  }
}
</style>
